<template>
  <div class="max-w-full w-full block overflow-ellipsis pr-2 truncate">
    <div v-if="!readOnly">
      <select
        v-if="['R0', 'R1'].includes(accountingPermissions)"
        class="max-w-full w-full"
        :class="{
          'bg-attention/25': warnEmptyNotAllowed,
          'bg-danger/25': warnOldCodeAdminOnly,
        }"
        :value="value ? value.id : null"
        @input="updateValue($event)"
        :title="
          warnEmptyNotAllowed
            ? 'Käibemaks ei tohi olla tühi või ei leitud vana koodiga käibemaksu!'
            : warnOldCodeAdminOnly
            ? 'Vana kood pole enam lubatud selles kohas!'
            : ''
        "
      >
        <option v-if="warnEmptyNotAllowed" :value="null" disabled>
          {{ `Käibemaks ei tohi olla tühi!` }}
        </option>
        <option v-if="warnOldCodeAdminOnly" :value="null" disabled>
          {{
            `Vana kood pole enam lubatud selles kohas! (${oldCodeObject.description})`
          }}
        </option>
        <option v-if="allowEmpty" :value="null" :selected="value === null">
          Puudub
        </option>
        <option
          v-for="taxType in filteredTaxTypes"
          :key="taxType.id"
          :value="taxType.id"
          :selected="value && taxType.id === value.id"
        >
          {{
            `${taxType.description} (${
              taxType.accounting_code ? taxType.accounting_code : "-"
            })`
          }}
        </option>
      </select>
      <select
        v-else
        class="max-w-full w-full"
        :class="{
          'bg-danger/25': warnOldCodeAdminOnly,
          'bg-attention/25': warnEmptyNotAllowed,
        }"
        :value="value ? value.id : null"
        @input="updateValue($event)"
        :title="
          warnEmptyNotAllowed
            ? 'Käibemaks ei tohi olla tühi või ei leitud vana koodiga käibemaksu!'
            : ''
        "
      >
        <option v-if="warnOldCodeAdminOnly" :value="null" disabled>
          {{
            `Vana KM kood (${this.oldCodeObject.description}) ainult adminitele`
          }}
        </option>
        <option v-if="warnEmptyNotAllowed" :value="null" disabled>
          {{
            "Käibemaks ei tohi olla tühi või ei leitud vana koodiga käibemaksu!"
          }}
        </option>
        <option v-if="allowEmpty" :value="null" :selected="value === null">
          Puudub
        </option>
        <option
          v-for="taxType in filteredTaxTypes.filter((x) => !x.only_admin)"
          :key="taxType.id"
          :value="taxType.id"
          :selected="value && taxType.id === value.id"
        >
          {{
            `${taxType.description} (${
              taxType.accounting_code ? taxType.accounting_code : "-"
            })`
          }}
        </option>
      </select>
    </div>
    <span
      v-else
      :class="{ 'bg-danger/25 p-1 rounded-md': warnOldCodeAdminOnly }"
      :title="
        warnOldCodeAdminOnly
          ? `Vana KM kood (${this.oldCodeObject.description}) ainult adminitele`
          : value
          ? `${value.description} (${
              value.accounting_code ? value.accounting_code : '-'
            })`
          : '-'
      "
    >
      {{
        value
          ? `${value.description} (${
              value.accounting_code ? value.accounting_code : "-"
            })`
          : "-"
      }}
    </span>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "CustomTaxSelector",
  props: {
    value: {
      type: Object,
      default: null,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    allowIncoming: {
      type: Boolean,
      default: true,
    },
    allowOutgoing: {
      type: Boolean,
      default: true,
    },
    allowEmpty: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      filteredTaxTypes: [],
      warnOldCodeAdminOnly: false,
      warnEmptyNotAllowed: false,
      oldCodeObject: null,
    };
  },
  methods: {
    updateValue(event) {
      // Find the tax type object by ID
      const taxTypeId = event.target.value;
      const selectedTaxType = this.filteredTaxTypes.find(
        (tax) => tax.id.toString() === taxTypeId.toString()
      );
      this.$emit("input", selectedTaxType);
      this.warnOldCodeAdminOnly = false;
    },
  },
  async mounted() {
    if (!this.taxTypes)
      await this.$store.dispatch("companyVariables/retrieveTaxTypes");

    let types = [];
    if (this.allowIncoming) {
      types = [...this.taxTypes.filter((x) => x.incoming_tax_code)];
    }
    if (this.allowOutgoing) {
      types = [...types, ...this.taxTypes.filter((x) => x.outgoing_tax_code)];
    }
    this.filteredTaxTypes = types.filter(
      (item, index, self) => self.findIndex((t) => t.id === item.id) === index
    );
    // If code is made admin only, warn user and set default value
    if (
      !["R0", "R1"].includes(this.accountingPermissions) &&
      this.value &&
      this.value.only_admin
    ) {
      this.warnOldCodeAdminOnly = true;
      this.oldCodeObject = this.value;
      if (!this.readOnly) {
        const defaultValue = this.taxTypes.find((x) => x.default);
        if (defaultValue) {
          this.$emit("input", defaultValue);
          return;
        } else {
          this.$emit("input", this.taxTypes[0]);
          return;
        }
      }
    }
    // If value exists but not allowed in context, warn user and set default value
    if (this.value) {
      const taxType = this.taxTypes.find((x) => x.id === this.value.id);
      if (!taxType.incoming_tax_code && !taxType.outgoing_tax_code) {
        this.warnOldCodeAdminOnly = true;
        this.oldCodeObject = taxType;
        const defaultValue = this.taxTypes.find((x) => x.default) ?? null;
        if (defaultValue) {
          this.$emit("input", defaultValue);
          return;
        } else {
          this.$emit("input", this.taxTypes[0]);
          return;
        }
      }
    }
    // If empty not allowed and value is empty, warn user and set default value
    if (!this.allowEmpty && !this.value) {
      this.warnEmptyNotAllowed = true;
      const defaultValue = this.taxTypes.find((x) => x.default);
      if (defaultValue) {
        this.$emit("input", defaultValue);
      } else {
        this.$emit("input", this.taxTypes[0]);
      }
    }
  },
  computed: {
    ...mapGetters({
      accountingPermissions: "companyData/userAccountingPermission",
      taxTypes: "companyVariables/taxTypes",
    }),
  },
  watch: {
    taxTypes() {
      let types = [];
      if (this.allowIncoming) {
        types = [...this.taxTypes.filter((x) => x.incoming_tax_code)];
      }
      if (this.allowOutgoing) {
        types = [...types, ...this.taxTypes.filter((x) => x.outgoing_tax_code)];
      }
      this.filteredTaxTypes = types.filter(
        (item, index, self) => self.findIndex((t) => t.id === item.id) === index
      );
    },
  },
};
</script>

<style scoped lang="scss"></style>
